// Gated upload memory diagnostics — attribute a cancel-time heap wedge to a specific holder.
//
// The uploader can wedge the tab in GC when a very large batch (e.g. 10k) is cancelled in-flight:
// heap climbs to multiple GB and doesn't come back. The upload manager itself is memory-bounded
// (proven by a cancel harness), so the retained bytes live in some OTHER structure. This module logs
// `performance.memory` alongside per-subsystem retained-byte probes on a timer and in a burst around
// cancel, so the console shows WHICH holder tracks the heap and whether it releases after cancel.
//
// Off by default — zero overhead unless explicitly enabled. Turn it on in the browser console:
//     localStorage.setItem('PHX_UPLOAD_DIAG', '1')   // then reload the page
// or set window.__PHX_UPLOAD_DIAG__ = true before a run. Disable with removeItem / '0'.

type ProbeResult = { items?: number; bytes?: number; note?: string };
type Probe = () => ProbeResult;

const probes = new Map<string, Probe>();

/** Register a subsystem probe. Returns an unregister fn. The probe must be cheap (called every ~2s). */
export function registerUploadProbe(name: string, probe: Probe): () => void {
  probes.set(name, probe);
  return () => {
    if (probes.get(name) === probe) probes.delete(name);
  };
}

// Lightweight byte accounting for transient buffers that no single collection holds (e.g. an
// ArrayBuffer read for a PUT body): increment on alloc, decrement when it can be freed.
const liveBytes = new Map<string, number>();
export function trackBytes(category: string, delta: number): void {
  if (!delta) return;
  liveBytes.set(category, (liveBytes.get(category) || 0) + delta);
}

let enabled: boolean | null = null;
/** Force the enabled state at runtime (used by the console hook so no reload is needed). */
export function setDiagEnabled(on: boolean): void {
  enabled = on;
}
export function diagEnabled(): boolean {
  if (enabled !== null) return enabled;
  try {
    const w = window as unknown as { __PHX_UPLOAD_DIAG__?: boolean };
    enabled =
      typeof window !== "undefined" &&
      (window.localStorage?.getItem("PHX_UPLOAD_DIAG") === "1" || w.__PHX_UPLOAD_DIAG__ === true);
  } catch {
    enabled = false;
  }
  return !!enabled;
}

const mb = (b: number | undefined | null): string => (b == null ? "—" : `${(b / 1048576).toFixed(0)}MB`);

function heap(): { used: number; total: number; limit: number } | null {
  const m = (performance as unknown as {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
  }).memory;
  return m ? { used: m.usedJSHeapSize, total: m.totalJSHeapSize, limit: m.jsHeapSizeLimit } : null;
}

// Live DOM-node census. The upload UI (both VirtualGrids) is windowed, so the count of MOUNTED nodes
// should stay roughly flat during an upload. A monotonically CLIMBING total while the UI is bounded
// means nodes are being retained after detachment (pinned by a JS reference) — the leak class behind
// "DOM nodes always climbing" + CPU-at-100%-on-cancel (GC finally walking the detached graph). The
// per-tag split localises it: a climb concentrated in <img>/<svg>/<canvas> points at per-file preview
// or icon elements that aren't being released; a climb in <div> points at row/container retention.
// `document.getElementsByTagName('*')` counts only attached nodes, so a rising number here is a lower
// bound on retention — detached nodes don't show, but a climb in the attached count is unambiguous.
function dom(): { total: number; img: number; svg: number; canvas: number; video: number } | null {
  try {
    if (typeof document === "undefined") return null;
    return {
      total: document.getElementsByTagName("*").length,
      img: document.getElementsByTagName("img").length,
      svg: document.getElementsByTagName("svg").length,
      canvas: document.getElementsByTagName("canvas").length,
      video: document.getElementsByTagName("video").length,
    };
  } catch {
    return null;
  }
}

const t0 = typeof performance !== "undefined" ? performance.now() : 0;

export function uploadDiagSnapshot(label: string): void {
  if (!diagEnabled()) return;
  const h = heap();
  const d = dom();
  const rows: Record<string, unknown>[] = [];
  let tracked = 0;
  for (const [name, probe] of probes) {
    try {
      const r = probe();
      tracked += r.bytes || 0;
      rows.push({
        subsystem: name,
        items: r.items ?? "",
        MB: r.bytes != null ? +(r.bytes / 1048576).toFixed(1) : "",
        note: r.note ?? "",
      });
    } catch (e) {
      rows.push({ subsystem: name, note: `probe err: ${String(e)}` });
    }
  }
  for (const [cat, b] of liveBytes) {
    if (!b) continue;
    tracked += b;
    rows.push({ subsystem: `bytes:${cat}`, MB: +(b / 1048576).toFixed(1) });
  }
  const dt = ((performance.now() - t0) / 1000).toFixed(1);
  // One headline line (always greppable) + a table of the holders. The DOM census rides on the
  // headline so a climbing node count is visible at a glance next to the heap, without expanding
  // the table — `nodes=` is the number to watch: flat ≈ bounded UI, rising = detached-node leak.
  const domStr = d
    ? ` | nodes=${d.total.toLocaleString()} img=${d.img} svg=${d.svg} canvas=${d.canvas} video=${d.video}`
    : "";
  console.log(
    `%c[uploadDiag +${dt}s] ${label} — heap used=${mb(h?.used)} total=${mb(h?.total)} limit=${mb(
      h?.limit,
    )} | tracked≈${mb(tracked)}${domStr}`,
    "color:#0366d6;font-weight:bold",
  );
  if (rows.length) {
    try {
      console.table(rows);
    } catch {
      console.log(rows);
    }
  }
}

// Synchronous breadcrumb — the ONE instrument that survives a frozen event loop. A setInterval
// snapshot can't fire while JS is stuck in a single uninterrupted operation (the exact signature of
// this freeze: logs stop, heap rising, CPU 100%), so the periodic ticker is blind to it. diagMark
// logs INLINE, the instant it's reached, so the last line printed before the console goes silent
// names the operation that stalled. Placed at the entry/exit of each teardown step and at the top of
// the hot render/flush paths, the trail distinguishes the two freeze classes at a glance:
//   - a RENDER/FLUSH STORM → the same mark fires thousands of times with an escalating count right up
//     to the freeze (an unbounded re-render loop, each render allocating → heap climbs);
//   - a SINGLE LONG SYNC OP → one mark prints, then silence (that operation never returned).
// To avoid flooding the console in the storm case, each label logs on a 1,2,4,8,…,1k,2k,… backoff
// plus every call once it's clearly looping, so the escalation is visible without drowning the trail.
const markCounts = new Map<string, number>();
export function diagMark(label: string, extra?: string): void {
  if (!diagEnabled()) return;
  const n = (markCounts.get(label) || 0) + 1;
  markCounts.set(label, n);
  // Log the first hits, then powers of two, then every 1000 — enough to see a storm escalate.
  const loud = n <= 8 || (n & (n - 1)) === 0 || n % 1000 === 0;
  if (!loud) return;
  const dt = ((performance.now() - t0) / 1000).toFixed(1);
  console.log(
    `%c[uploadDiag mark +${dt}s] ${label} #${n}${extra ? " " + extra : ""}`,
    "color:#b8860b;font-weight:bold",
  );
}
/** Reset a mark's counter (call when a fresh cancel/run begins so its trail starts at #1). */
export function diagMarkReset(prefix: string): void {
  for (const k of [...markCounts.keys()]) if (k.startsWith(prefix)) markCounts.delete(k);
}

let ticker: ReturnType<typeof setInterval> | null = null;

/** Begin periodic snapshots (every 2s) while a batch runs. Idempotent. */
export function uploadDiagStart(reason: string): void {
  if (!diagEnabled() || ticker != null) return;
  uploadDiagSnapshot(`start:${reason}`);
  ticker = setInterval(() => uploadDiagSnapshot("tick"), 2000);
}

/** Stop the periodic ticker and take a final snapshot. */
export function uploadDiagStop(reason: string): void {
  if (ticker != null) {
    clearInterval(ticker);
    ticker = null;
  }
  uploadDiagSnapshot(`stop:${reason}`);
}

/** A burst of snapshots around a cancel, to see whether the heap actually drops once GC runs. */
export function uploadDiagCancelBurst(reason: string): void {
  if (!diagEnabled()) return;
  uploadDiagSnapshot(`cancel:${reason}:t0`);
  for (const d of [500, 1500, 4000, 10000, 20000]) {
    setTimeout(() => uploadDiagSnapshot(`cancel:${reason}:+${d}ms`), d);
  }
}

// Console hook — so the diagnostic can be confirmed live and driven WITHOUT reloading or running an
// upload. In the browser console:
//   __uploadDiag.status()           // is it loaded? enabled? is performance.memory available?
//   __uploadDiag.enable()           // turn on now (also persists the flag) — no reload needed
//   __uploadDiag.snapshot('manual') // take one snapshot immediately
//   __uploadDiag.disable()
// If `__uploadDiag` is undefined in the console, the page is running an OLD bundle — hard-reload
// (Cmd/Ctrl+Shift+R) or disable the DevTools cache; the server build already carries this.
(function installConsoleHook() {
  try {
    if (typeof window === "undefined") return;
    const w = window as unknown as { __uploadDiag?: unknown };
    w.__uploadDiag = {
      status(): void {
        const hasMem = !!(performance as unknown as { memory?: unknown }).memory;
        const names = [...probes.keys()];
        console.log(
          `%c[uploadDiag] loaded. enabled=${diagEnabled()} performance.memory=${hasMem} probes=[${names.join(
            ", ",
          )}]`,
          "color:#0366d6;font-weight:bold",
        );
        if (!hasMem) {
          console.log(
            "[uploadDiag] performance.memory is Chromium-only — heap numbers show '—' on other browsers; the per-holder MB still work.",
          );
        }
        uploadDiagSnapshot("status");
      },
      enable(): void {
        try {
          window.localStorage?.setItem("PHX_UPLOAD_DIAG", "1");
        } catch {
          /* private mode */
        }
        setDiagEnabled(true);
        console.log("%c[uploadDiag] enabled (no reload needed). Run your upload, then cancel.", "color:#0a0");
        uploadDiagSnapshot("enabled");
      },
      disable(): void {
        try {
          window.localStorage?.removeItem("PHX_UPLOAD_DIAG");
        } catch {
          /* ignore */
        }
        setDiagEnabled(false);
        console.log("[uploadDiag] disabled.");
      },
      snapshot(label = "manual"): void {
        const was = diagEnabled();
        if (!was) setDiagEnabled(true); // allow a one-off snapshot even when not globally enabled
        uploadDiagSnapshot(label);
        if (!was) setDiagEnabled(false);
      },
    };
    // Immediate banner when already enabled, so a reload with the flag set confirms the code is live
    // even before any upload runs.
    if (diagEnabled()) {
      console.log("%c[uploadDiag] active — ticks every 2s while uploading, burst on cancel.", "color:#0366d6");
    }
  } catch {
    /* never let diagnostics break the app */
  }
})();
